import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { routerStore, Link } from 'core/routing';
import { CardIndicator } from 'legacyComponents/cards/types/indicator';
import {
  PostCard,
  TextWrapper,
  FullWidthTitle as Title,
  DiscussLink,
  PostCardActions,
  pseudoCardStyles,
} from 'legacyComponents/cards/styles';
import { Date } from 'apps/components/date';
import { conf as mainConf } from 'apps/config.yaml';
import { imageSrc } from 'apps/file/utils';

import { withNamespaces, Trans } from 'react-i18next';
import { Button, Tooltip, Paragraph, Icon } from 'components';
import { Avatar, Username } from 'apps/components/shared';

export const ThreadCard = withNamespaces()(
  observer(({ cardObject, cardNo, cardColumns }) => {
    const noMargin = (cardNo + 1) % cardColumns === 0;
    if (cardObject === null) {
      return <CardIndicator noMargin={noMargin} type='issue' />;
    }

    const {
      title,
      category,
      creatorProfile,
      commentsCount,
      lastActivityAt,
      urlId,
      forum,
      membersOnly,
      content,
    } = cardObject;

    let threadRoute = routerStore.route.name;
    let currentParams = routerStore.route.params;
    let threadParams = Object.assign({}, currentParams, { thread: urlId });
    let threadPageParams = Object.assign({}, currentParams, {
      threadId: urlId,
    });
    let categoryParams = Object.assign({}, currentParams, {
      categoryId: category.urlId,
    });
    if (!threadRoute.includes('initiative') && !content.whitelabel) {
      threadRoute = 'initiative.forum.thread';
      threadParams = Object.assign(
        {},
        {
          initiative: `+${forum.parentContent.slug}`,
          categoryId: category.urlId,
          threadId: urlId,
        },
      );
      threadPageParams = Object.assign(
        {},
        {
          initiative: `+${forum.parentContent.slug}`,
          categoryId: category.urlId,
          threadId: urlId,
        },
      );
      categoryParams = Object.assign(
        {},
        {
          initiative: `+${forum.parentContent.slug}`,
          categoryId: category.urlId,
        },
      );
    }
    if (!threadRoute.includes('initiative') && content.whitelabel) {
      // Ensure we always link to thread page when re-rotuing a forum
      // to some microsite sub page.
      threadRoute = 'initiative.forum.thread';
      currentParams = {
        initiative: cardObject.manager.manager.params.get('initiative'),
      };
      threadParams = Object.assign({}, currentParams, { threadId: urlId });
      threadPageParams = Object.assign({}, currentParams, {
        threadId: urlId,
      });
      categoryParams = Object.assign({}, currentParams, {
        categoryId: category.urlId,
      });
    }

    const categoryName = category.title;
    const creatorUsername = creatorProfile.username;
    const displayColor =
      category.color === null ? '#cdcdcd' : `#${category.color}`;
    const parentSlug = forum && forum.parentContent.slug;

    return (
      <PostCard
        colorCard={displayColor}
        data-intercom-target={cardNo === 0 ? 'organisation-pinned-list' : ''}
      >
        <Link name='profile' params={{ profile: creatorProfile.username }}>
          <Avatar
            src={imageSrc(
              creatorProfile.avatar,
              creatorProfile.imageFallbackPath,
              mainConf.imgSizes.profile.small,
            )}
          />
        </Link>
        <TextWrapper>
          <Link name={threadRoute} params={threadParams} content={content}>
            <Title>{title}</Title>
          </Link>
          <Paragraph size='small'>
            {membersOnly && <IconStyled name='locked' size='small' />}
            <Trans>
              {forum && (
                <>
                  <Link
                    name='initiative'
                    params={{ initiative: `+${parentSlug}` }}
                  >
                    <Category>+{{ parentSlug }}</Category>
                  </Link>
                  <IconCaretStyled name='arrow_right' size='xsmall' />
                </>
              )}
              <Link
                name='initiative.forum.category'
                params={categoryParams}
                content={content}
              >
                <Category colorCategory={displayColor}>
                  {{ categoryName }}
                </Category>
              </Link>{' '}
              by{' '}
              <UsernameStyled
                name='profile'
                params={{ profile: creatorUsername }}
              >
                {{ creatorUsername }}
              </UsernameStyled>
            </Trans>
            <Trans>
              last activity <DateStyled value={lastActivityAt} />
            </Trans>
            {content.likesCount.toString()}{' '}
            <Trans>{content.likesCount === 1 ? 'Like' : 'Likes'}</Trans>
            <DiscussLinkStyled
              name={threadRoute}
              params={threadParams}
              content={content}
            >
              {commentsCount > 0 ? (
                <Trans count={commentsCount}>{{ commentsCount }} comment</Trans>
              ) : (
                <Trans>Discuss</Trans>
              )}
            </DiscussLinkStyled>
          </Paragraph>
        </TextWrapper>
        <PostCardActions>
          <Tooltip id='tooltipThreads' delayShow={500} />
          <Button
            variant='secondary'
            active={content.isLiked}
            icon={content.isLiked ? 'liked' : 'unliked'}
            onClick={() =>
              content.isLiked ? content.UnLike() : content.Like()
            }
            ariaLabel={content.isLiked ? 'Unlike' : 'Like'}
            data-tip={content.isLiked ? 'Unlike' : 'Like'}
            data-for='tooltipThreads'
            outline
          />
          <Link
            name='initiative.forum.thread'
            params={Object.assign({}, threadPageParams)}
            content={content}
          >
            <Button
              variant='secondary'
              icon='launch'
              ariaLabel='See thread'
              data-tip='See thread'
              data-for='tooltipThreads'
              outline
            />
          </Link>
        </PostCardActions>
      </PostCard>
    );
  }),
);

const IconStyled = styled(Icon)`
  margin-right: ${props => props.theme.spacing.xsmall};
  vertical-align: top;
`;

const IconCaretStyled = styled(IconStyled)`
  margin-left: ${props => props.theme.spacing.xsmall};
  vertical-align: middle;
`;

const Category = styled.span`
  color: ${props => props.theme.color.wfGrey700};
  &:hover {
    text-decoration: underline;
    color: ${props => props.colorCategory && props.colorCategory};
  }
`;

const DateStyled = styled(Date)`
  &::after {
    ${pseudoCardStyles}
  }
`;

const DiscussLinkStyled = styled(DiscussLink)`
  &::before {
    ${pseudoCardStyles}
  }
`;

const UsernameStyled = styled(Username)`
  &::after {
    ${pseudoCardStyles}
    font-weight: ${props => props.theme.font.weight.regular};
  }
`;

ThreadCard.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  author: PropTypes.string,
  datePublished: PropTypes.string,
  comments: PropTypes.array,
};
